import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ChevronDown, ChevronRight, Search, X, RefreshCw, Users, BookOpen, Filter, LayoutGrid } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { gridsService } from '../../services/gridsService';
import { staffService, StaffMember } from '../../services/staffService';
import { useSchoolConfig } from '../../contexts/SchoolConfigContext';
import { parseClassCode, ClassCodeComponents } from '../../utils/classCodeParser';
import { StaffAllocationGrid, ClassAllocation } from '../../types/grid';
import { displayLearningArea } from '../../constants/learningAreas';

interface StaffTeachingAssignment {
  classCode: string;
  className: string;
  lineId: string;
  yearLevel?: number;
  level: ClassCodeComponents['level'];
  curriculumArea: string;
  gridName: string;
}

interface StaffTeachingData {
  staffId: string;
  staffName: string;
  staffCode: string;
  department: string;
  fte: number;
  assignments: StaffTeachingAssignment[];
  levels: Set<string>;
  yearLevels: Set<number>;
  curriculumAreas: Set<string>; // Curriculum areas from their assignments
  classCount: number;
}

const StaffByTeaching: React.FC = () => {
  const { addToast } = useToast();
  const { levelCodes, curriculumAreas } = useSchoolConfig();
  const navigate = useNavigate();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [staffTeachingData, setStaffTeachingData] = useState<StaffTeachingData[]>([]);
  const [expandedStaffId, setExpandedStaffId] = useState<string | null>(null);
  const [hasGrids, setHasGrids] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set());
  const [selectedYearLevels, setSelectedYearLevels] = useState<Set<number>>(new Set());
  const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set());

  // Available filter options
  const [availableYearLevels, setAvailableYearLevels] = useState<number[]>([]);

  // Curriculum areas for filter (sorted by display order)
  const availableCurriculumAreas = useMemo(() => {
    if (!curriculumAreas || curriculumAreas.length === 0) return [];
    return curriculumAreas
      .filter(ca => ca.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [curriculumAreas]);

  // Helper to check if a class curriculum area matches a central curriculum area
  // Handles cases like class="Arts" matching central="Arts / STEAM / Religion"
  const matchesCurriculumArea = useCallback((classCurriculumArea: string, centralCurriculumArea: { code: string; name: string }) => {
    if (!classCurriculumArea) return false;
    const classCA = classCurriculumArea.toLowerCase().trim();
    const centralName = centralCurriculumArea.name.toLowerCase();
    const centralCode = centralCurriculumArea.code.toLowerCase();

    // Exact match on name or code
    if (classCA === centralName || classCA === centralCode) return true;

    // Check if class CA is contained in a multi-area name (split by " / ")
    const parts = centralCurriculumArea.name.split(' / ').map(p => p.toLowerCase().trim());
    return parts.includes(classCA);
  }, []);

  // Level definitions for filter options
  const levelDefinitions = useMemo(() => {
    // Use school config level codes if available, otherwise fall back to defaults
    if (levelCodes && levelCodes.length > 0) {
      return levelCodes
        .filter(lc => lc.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(lc => ({
          code: lc.code,
          name: lc.name
        }));
    }
    // Fallback to common level definitions
    return [
      { code: 'DI', name: 'Discover' },
      { code: 'EN', name: 'Engage' },
      { code: 'EX', name: 'Explore' },
      { code: 'FO', name: 'Focus' },
      { code: 'RE', name: 'Reach' },
      { code: '12', name: 'Units 1 & 2' },
      { code: '34', name: 'Units 3 & 4' },
      { code: 'VMA', name: 'Vocational Major' },
      { code: 'ORA', name: 'Oratory' }
    ];
  }, [levelCodes]);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Fetch grids and staff in parallel
      const [grids, allStaff] = await Promise.all([
        gridsService.getAllGrids(),
        staffService.getAllStaff()
      ]);

      // Build a map of staff code -> staff member
      const staffByCode = new Map<string, StaffMember>();
      allStaff.forEach(staff => {
        const code = staff.staff_code || staff.initial;
        if (code) {
          staffByCode.set(code.toUpperCase(), staff);
        }
      });

      // Build staff teaching data from grid assignments
      const staffDataMap = new Map<string, StaffTeachingData>();
      const yearLevelSet = new Set<number>();

      // Filter to only active grids
      const activeGrids = grids.filter(grid => grid.isActive !== false);
      setHasGrids(activeGrids.length > 0);

      activeGrids.forEach((grid: StaffAllocationGrid) => {
        if (!grid.classes) return;

        // Filter out composite sub-classes to prevent double-counting
        const validClasses = grid.classes.filter(c => !c.isCompositeSubClass);

        validClasses.forEach((classItem: ClassAllocation) => {
          if (!classItem.lineAssignments) return;

          // Parse the class code to extract level information
          const parsedCode = parseClassCode(classItem.className);

          // Extract year level from class or parsed code (with NaN check)
          const parsedYear = parsedCode.identifier ? parseInt(parsedCode.identifier) : NaN;
          const yearLevel = classItem.yearLevel || (isNaN(parsedYear) ? undefined : parsedYear);

          if (yearLevel) {
            yearLevelSet.add(yearLevel);
          }

          // Process each line assignment
          Object.entries(classItem.lineAssignments).forEach(([lineId, assignmentValue]) => {
            if (!assignmentValue) return;

            // Extract teacher codes - handle both simple strings and JSON multi-teacher assignments
            const teacherCodes: string[] = [];

            if (typeof assignmentValue === 'string') {
              // Check if it's a JSON string (multi-teacher assignment)
              if (assignmentValue.startsWith('[') || assignmentValue.startsWith('{')) {
                try {
                  const parsed = JSON.parse(assignmentValue);
                  const assignments = Array.isArray(parsed) ? parsed : [parsed];
                  assignments.forEach((a: any) => {
                    // Handle both TEACHERCODE and teacherCode formats
                    const code = a.TEACHERCODE || a.teacherCode || a.TeacherCode;
                    if (code) {
                      teacherCodes.push(code);
                    }
                  });
                } catch {
                  // If JSON parsing fails, treat as simple teacher code
                  teacherCodes.push(assignmentValue);
                }
              } else {
                // Simple string teacher code
                teacherCodes.push(assignmentValue);
              }
            }

            // Process each teacher code found
            teacherCodes.forEach(teacherCode => {
              const normalizedCode = teacherCode.toUpperCase();
              const staffMember = staffByCode.get(normalizedCode);

              // Get or create staff data entry
              let staffData = staffDataMap.get(normalizedCode);
              if (!staffData) {
                staffData = {
                  staffId: staffMember?.id || normalizedCode,
                  staffName: staffMember?.name || normalizedCode,
                  staffCode: normalizedCode,
                  department: staffMember?.department || staffMember?.curriculum_area || 'Unknown',
                  fte: staffMember?.fte || 0,
                  assignments: [],
                  levels: new Set(),
                  yearLevels: new Set(),
                  curriculumAreas: new Set(),
                  classCount: 0
                };
                staffDataMap.set(normalizedCode, staffData);
              }

              // Get curriculum area from the class
              const classCurriculumArea = classItem.curriculumArea || parsedCode.faculty.name;

              // Add assignment
              const assignment: StaffTeachingAssignment = {
                classCode: classItem.className,
                className: classItem.className,
                lineId,
                yearLevel,
                level: parsedCode.level,
                curriculumArea: classCurriculumArea,
                gridName: grid.name
              };

              staffData.assignments.push(assignment);
              staffData.levels.add(parsedCode.level.code);
              if (yearLevel) {
                staffData.yearLevels.add(yearLevel);
              }
              // Track curriculum area from assignments
              if (classCurriculumArea) {
                staffData.curriculumAreas.add(classCurriculumArea);
              }
              staffData.classCount++;
            });
          });
        });
      });

      // Convert map to array and sort by name
      const staffDataArray = Array.from(staffDataMap.values())
        .sort((a, b) => a.staffName.localeCompare(b.staffName));

      setStaffTeachingData(staffDataArray);
      setAvailableYearLevels(Array.from(yearLevelSet).sort((a, b) => a - b));

    } catch (error) {
      console.error('Error loading staff teaching data:', error);
      addToast('Failed to load staff teaching data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter staff data
  const filteredStaffData = useMemo(() => {
    return staffTeachingData.filter(staff => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          staff.staffName.toLowerCase().includes(query) ||
          staff.staffCode.toLowerCase().includes(query) ||
          staff.department.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Level filter (must have at least one matching level)
      if (selectedLevels.size > 0) {
        const hasMatchingLevel = Array.from(selectedLevels).some(level =>
          staff.levels.has(level)
        );
        if (!hasMatchingLevel) return false;
      }

      // Year level filter (must have at least one matching year level)
      if (selectedYearLevels.size > 0) {
        const hasMatchingYearLevel = Array.from(selectedYearLevels).some(yearLevel =>
          staff.yearLevels.has(yearLevel)
        );
        if (!hasMatchingYearLevel) return false;
      }

      // Curriculum area filter (must have at least one matching curriculum area)
      if (selectedDepartments.size > 0) {
        // Find the selected central curriculum areas
        const selectedCAs = availableCurriculumAreas.filter(ca => selectedDepartments.has(ca.name));

        // Check if any of the staff's class curriculum areas match any selected central CA
        const hasMatchingCurriculumArea = Array.from(staff.curriculumAreas).some(classCurriculumArea =>
          selectedCAs.some(centralCA => matchesCurriculumArea(classCurriculumArea, centralCA))
        );
        if (!hasMatchingCurriculumArea) return false;
      }

      return true;
    });
  }, [staffTeachingData, searchQuery, selectedLevels, selectedYearLevels, selectedDepartments, availableCurriculumAreas, matchesCurriculumArea]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedLevels(new Set());
    setSelectedYearLevels(new Set());
    setSelectedDepartments(new Set());
  };

  // Toggle filter selection
  const toggleLevel = (code: string) => {
    setSelectedLevels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  const toggleYearLevel = (yearLevel: number) => {
    setSelectedYearLevels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(yearLevel)) {
        newSet.delete(yearLevel);
      } else {
        newSet.add(yearLevel);
      }
      return newSet;
    });
  };

  const toggleDepartment = (dept: string) => {
    setSelectedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dept)) {
        newSet.delete(dept);
      } else {
        newSet.add(dept);
      }
      return newSet;
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Staff Name', 'Staff Code', 'Learning Area', 'FTE', 'Classes Count', 'Levels', 'Year Levels', 'Class Details'];

    const rows = filteredStaffData.map(staff => {
      const levelsStr = Array.from(staff.levels).join(', ');
      const yearLevelsStr = Array.from(staff.yearLevels).sort((a, b) => a - b).join(', ');
      const classDetails = staff.assignments
        .map(a => `${a.classCode} (Line ${a.lineId})`)
        .join('; ');

      return [
        staff.staffName,
        staff.staffCode,
        staff.department,
        staff.fte.toFixed(2),
        staff.classCount.toString(),
        levelsStr,
        yearLevelsStr,
        classDetails
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `staff-by-teaching-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    // Clean up the blob URL to prevent memory leak
    setTimeout(() => URL.revokeObjectURL(url), 100);

    addToast('CSV exported successfully', 'success');
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || selectedLevels.size > 0 || selectedYearLevels.size > 0 || selectedDepartments.size > 0;

  return (
    <div>
      <PageHeader
        title="Staff by Teaching"
        description="View staff members and their teaching assignments filtered by level, year, and learning area"
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={loadData}
              loading={isLoading}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={exportToCSV}
              disabled={filteredStaffData.length === 0}
            >
              Export CSV
            </Button>
          </div>
        }
      />

      {/* Filters Card — hidden when there is no active grid to filter */}
      {(isLoading || hasGrids) && (
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-ink-500" />
            <h3 className="text-card-title text-ink-900">Filters</h3>
          </div>
          {hasActiveFilters && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<X className="h-4 w-4" />}
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-ink-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
              placeholder="Search by name, code, or learning area..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Level Filter */}
          <div>
            <h4 className="text-body-sm font-medium text-ink-700 mb-2">Level</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {levelDefinitions.map(level => (
                <label
                  key={level.code}
                  className="flex items-center gap-2 cursor-pointer hover:bg-ink-50 p-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedLevels.has(level.code)}
                    onChange={() => toggleLevel(level.code)}
                    className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                  />
                  <span className="text-body-sm text-ink-700">{level.code} - {level.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Year Level Filter */}
          <div>
            <h4 className="text-body-sm font-medium text-ink-700 mb-2">Year Level</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {availableYearLevels.map(yearLevel => (
                <label
                  key={yearLevel}
                  className="flex items-center gap-2 cursor-pointer hover:bg-ink-50 p-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedYearLevels.has(yearLevel)}
                    onChange={() => toggleYearLevel(yearLevel)}
                    className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                  />
                  <span className="text-body-sm text-ink-700">Year {yearLevel}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Curriculum Area Filter */}
          <div>
            <h4 className="text-body-sm font-medium text-ink-700 mb-2">Learning Area</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {availableCurriculumAreas.map(ca => (
                <label
                  key={ca.id}
                  className="flex items-center gap-2 cursor-pointer hover:bg-ink-50 p-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedDepartments.has(ca.name)}
                    onChange={() => toggleDepartment(ca.name)}
                    className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                  />
                  <span className="text-body-sm text-ink-700">{ca.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* Results */}
      {isLoading ? (
        <Card className="p-8">
          <div className="flex justify-center py-8">
            <Spinner size="lg" label="Loading staff teaching data…" />
          </div>
        </Card>
      ) : !hasGrids ? (
        <Card className="p-8 text-center">
          <LayoutGrid className="h-12 w-12 text-ink-400 mx-auto mb-4" />
          <h3 className="text-card-title text-ink-900 mb-2">No Active Grid Found</h3>
          <p className="text-ink-500 mb-4 max-w-md mx-auto">
            Staff teaching assignments are read from the timetable grid. Once a grid is
            imported and set as active, every teacher's classes will appear here.
          </p>
          <Button
            variant="primary"
            leftIcon={<LayoutGrid className="h-4 w-4" />}
            onClick={() => navigate('/grid/multi-editor')}
          >
            Open Timetable Editor
          </Button>
        </Card>
      ) : (
        <Card>
          {/* Summary */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-navy-500" />
                <span className="text-body-sm text-ink-500">
                  Showing <span className="font-semibold text-ink-900">{filteredStaffData.length}</span> of{' '}
                  <span className="font-semibold text-ink-900">{staffTeachingData.length}</span> staff
                </span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-ok-fg" />
                <span className="text-body-sm text-ink-500">
                  <span className="font-semibold text-ink-900">
                    {filteredStaffData.reduce((sum, s) => sum + s.classCount, 0)}
                  </span>{' '}
                  total assignments
                </span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-200">
              <thead className="bg-ink-50">
                <tr>
                  <th className="w-8 px-4 py-3"></th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Staff Name
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Learning Area
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    FTE
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Classes
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Levels
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Year Levels
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-200">
                {filteredStaffData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center">
                      {hasActiveFilters ? (
                        <div>
                          <p className="text-ink-700 font-medium mb-1">No matching staff</p>
                          <p className="text-ink-500 text-body-sm mb-3">
                            No staff match the selected filters. Try widening or clearing them.
                          </p>
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<X className="h-4 w-4" />}
                            onClick={clearFilters}
                          >
                            Clear Filters
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <p className="text-ink-700 font-medium mb-1">No Teaching Assignments Yet</p>
                          <p className="text-ink-500 text-body-sm">
                            The active grid has no teachers assigned to classes. Assign staff in
                            the Timetable Editor and they'll appear here.
                          </p>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredStaffData.map(staff => (
                    <React.Fragment key={staff.staffId}>
                      {/* Main row */}
                      <tr
                        className="hover:bg-ink-50 cursor-pointer"
                        onClick={() => setExpandedStaffId(
                          expandedStaffId === staff.staffId ? null : staff.staffId
                        )}
                      >
                        <td className="px-4 py-3">
                          {expandedStaffId === staff.staffId ? (
                            <ChevronDown className="h-4 w-4 text-ink-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-ink-500" />
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-body-sm font-medium text-ink-900">
                            {staff.staffName}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 text-meta font-mono tabular-nums bg-ink-100 rounded">
                            {staff.staffCode}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500">
                          {displayLearningArea(staff.department)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                          {staff.fte.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 text-meta font-semibold bg-navy-100 text-navy-800 rounded-full font-mono tabular-nums">
                            {staff.classCount}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500">
                          <div className="flex flex-wrap gap-1">
                            {Array.from(staff.levels).sort().map(level => (
                              <span
                                key={level}
                                className="px-1.5 py-0.5 text-meta bg-navy-100 text-navy-700 rounded"
                              >
                                {level}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                          {Array.from(staff.yearLevels).sort((a, b) => a - b).join(', ')}
                        </td>
                      </tr>

                      {/* Expanded details row */}
                      {expandedStaffId === staff.staffId && (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 bg-ink-50">
                            <div className="ml-8">
                              <h4 className="text-body-sm font-medium text-ink-700 mb-3">
                                Class Breakdown ({staff.assignments.length} assignments)
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-ink-200 border rounded-lg">
                                  <thead className="bg-ink-100">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase">
                                        Class Code
                                      </th>
                                      <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase">
                                        Line
                                      </th>
                                      <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase">
                                        Level
                                      </th>
                                      <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase">
                                        Year
                                      </th>
                                      <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase">
                                        Learning Area
                                      </th>
                                      <th className="px-3 py-2 text-left text-meta font-medium text-ink-500 uppercase">
                                        Grid
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-ink-200">
                                    {staff.assignments
                                      .sort((a, b) => a.classCode.localeCompare(b.classCode))
                                      .map((assignment, idx) => (
                                        <tr key={`${assignment.classCode}-${assignment.lineId}-${idx}`}>
                                          <td className="px-3 py-2 whitespace-nowrap text-body-sm font-mono tabular-nums text-ink-900">
                                            {assignment.classCode}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                            Line {assignment.lineId}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-body-sm">
                                            <span className="px-1.5 py-0.5 text-meta bg-navy-100 text-navy-700 rounded">
                                              {assignment.level.code}
                                            </span>
                                            <span className="ml-1 text-ink-500">
                                              {assignment.level.name}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                            {assignment.yearLevel || '-'}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-body-sm text-ink-500">
                                            {displayLearningArea(assignment.curriculumArea)}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-body-sm text-ink-500">
                                            {assignment.gridName}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default StaffByTeaching;
